import store from '../store';
import Events from '../constants/events';
import { handleError } from '../utilities';

type WpConsentValue = 'allow' | 'deny' | 'not set';
type WpConsentCategory =
  | 'functional'
  | 'preferences'
  | 'statistics'
  | 'statistics-anonymous'
  | 'marketing';

declare global {
  interface Window {
    wp?: unknown;
    wp_consent_type?: 'optin' | 'optout';
    wp_has_consent?: (category: WpConsentCategory) => boolean;
    wp_set_consent?: (
      category: WpConsentCategory,
      value: WpConsentValue,
    ) => void;
  }
}

const WP_MAPPING: Record<WpConsentCategory, string> = {
  functional: 'functional',
  preferences: 'functional',
  statistics: 'analytics',
  'statistics-anonymous': 'analytics',
  marketing: 'marketing',
};

function detectWPConsentAPI(): boolean {
  // Only detect when WordPress Consent Level API plugin is actually available
  return !!(
    typeof window.wp_has_consent === 'function' &&
    typeof window.wp_set_consent === 'function'
  );
}

function mapConsent(
  allowState: Record<string, boolean> = {},
): Record<string, WpConsentValue> {
  const result: Record<string, WpConsentValue> = {};
  Object.keys(WP_MAPPING).forEach((wp: string) => {
    const concord = WP_MAPPING[wp as WpConsentCategory];
    const val = allowState[concord];
    result[wp] = val === true ? 'allow' : val === false ? 'deny' : 'not set';
  });
  return result;
}

function syncConcordToWP(): void {
  try {
    const allowState = store.consentState.allowState || {};
    const wpMappedState = mapConsent(allowState);

    // Update WordPress Consent API with current Concord state
    Object.entries(wpMappedState).forEach(([category, value]) => {
      if (window.wp_set_consent) {
        window.wp_set_consent(category as WpConsentCategory, value);
      }
    });
  } catch (err) {
    handleError(err as Error);
  }
}

function setupWordPressIntegration(): {
  isWordPressConsentAPI: boolean;
  syncConcordToWP: () => void;
} | null {
  try {
    // Set consent type based on Concord's configuration
    const config = store.config as unknown as {
      regionTemplate?: { settings?: { consent?: { consentMode?: string } } };
    };
    const consentMode = config?.regionTemplate?.settings?.consent?.consentMode;

    // Only proceed if we have a valid consentMode
    if (consentMode === 'express') {
      window.wp_consent_type = 'optin';
    } else if (consentMode === 'implied') {
      window.wp_consent_type = 'optout';
    } else {
      // Exit if consentMode is missing or invalid
      return null;
    }

    // Dispatch event when consent type is defined (required by WordPress Consent API)
    document.dispatchEvent(new CustomEvent('wp_consent_type_defined'));

    // Initialize current Concord state in WordPress Consent API
    syncConcordToWP();

    // Listen for Concord consent changes and sync to WP
    window.addEventListener(Events.ConsentStateChanged, syncConcordToWP);

    return {
      isWordPressConsentAPI: true,
      syncConcordToWP,
    };
  } catch (err) {
    handleError(err instanceof Error ? err : new Error(String(err)));
    return null;
  }
}

export function initializeWordPress(): {
  isWordPressConsentAPI: boolean;
  syncConcordToWP: () => void;
} | null {
  // Try immediate detection first
  if (detectWPConsentAPI()) {
    return setupWordPressIntegration();
  }

  // If not available, listen for DOM ready and try again
  const initWhenReady = () => {
    if (detectWPConsentAPI()) {
      setupWordPressIntegration();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady, {
      once: true,
    });
  } else {
    // DOM is already ready, try a short delay
    setTimeout(initWhenReady, 100);
  }

  return null;
}
